import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Avatar from 'react-avatar';
import toast from 'react-hot-toast';
import {
  Activity,
  AtSign,
  Building2,
  Download,
  EyeOff,
  FileText,
  Flag,
  Globe,
  Link2,
  ListChecks,
  Lock,
  MessageSquare,
  MoreVertical,
  Paperclip,
  Pencil,
  Pin,
  PinOff,
  ThumbsUp,
  Users
} from 'lucide-react';
import {
  editPost,
  createLike,
  deleteLike,
  reportPost,
  setPostHidden,
  setPostPinned
} from '../services/api/implementation/impl';
import Slider from 'react-slick';
import { TONE_SOFT } from '../../shared/components/ui/primitives/tone';
import MentionText from '../components/feed/MentionText';
import LikersPopover from '../components/feed/LikersPopover';
import ReportPostDialog from '../components/feed/ReportPostDialog';

// Import slick styles (required)
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

// The verb decides the colour, so a feed of mixed activity scans by what
// happened: things made, things changed, things removed.
export const toneForAction = (label = '') => {
  const lower = label.toLowerCase();
  if (lower.includes('created')) return 'success';
  if (lower.includes('updated')) return 'info';
  if (lower.includes('deleted')) return 'danger';
  return 'brand';
};

// Left edge in the same colour as the action chip. Spelled out so Tailwind
// keeps the classes.
export const TONE_EDGE = {
  neutral: 'border-l-hairline-strong',
  success: 'border-l-success',
  info: 'border-l-info',
  danger: 'border-l-danger',
  brand: 'border-l-brand-600'
};

const parseDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

export const fullTimestamp = (value) => {
  const date = parseDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
};

// Recent activity reads best as an age; past a week the age stops meaning
// anything ("19d 21h") and the date is what someone is looking for.
export const timeAgo = (value) => {
  const date = parseDate(value);
  if (!date) return '';
  const minutes = Math.floor((Date.now() - date.getTime()) / (1000 * 60));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    ...(date.getFullYear() !== new Date().getFullYear() && { year: 'numeric' })
  }).format(date);
};

// The action chip: from module/method, else guessed from the post text.
export const tagInfoFor = (data) => {
  const module = (data?.module || '').toString().trim();
  const methodRaw = (data?.method || '').toString().trim();

  const normalizeMethod = (m) => {
    const lower = m.toLowerCase();
    if (!m) return '';
    if (lower === 'post') return 'Created';
    if (lower === 'create') return 'Created';
    if (lower === 'update' || lower === 'put' || lower === 'patch') return 'Updated';
    if (lower === 'delete' || lower === 'remove') return 'Deleted';
    return m.charAt(0).toUpperCase() + m.slice(1).toLowerCase();
  };

  const iconFor = (mod, content) => {
    const text = `${mod} ${content || ''}`.toLowerCase();
    const modLower = mod.toLowerCase();
    if (modLower === 'partner' || modLower === 'lead' || text.includes('lead')) return Users;
    if (text.includes('rfq') || text.includes('quotation')) return FileText;
    if (text.includes('requirement')) return FileText;
    if (text.includes('block') || text.includes('level') || text.includes('unit')) return Building2;
    if (text.includes('task')) return ListChecks;
    return Activity;
  };

  // Primary: use module + method
  if (module && methodRaw) {
    const method = normalizeMethod(methodRaw);
    return { label: `${method} ${module}`, icon: iconFor(module, '') };
  }

  // Fallback: derive from post text
  const postText = (data?.post || '').toLowerCase();
  if (postText) {
    if (postText.includes('rfq')) return { label: 'RFQ Created', icon: iconFor('rfq', postText) };
    if (postText.includes('quotation'))
      return { label: 'Quotation Created', icon: iconFor('quotation', postText) };
    if (postText.includes('requirement'))
      return { label: 'Requirement Created', icon: iconFor('requirement', postText) };
    if (postText.includes('task update'))
      return { label: 'Task Updated', icon: iconFor('task', postText) };
    if (postText.includes('block'))
      return { label: 'Block Updated', icon: iconFor('block', postText) };
    if (postText.includes('level'))
      return { label: 'Level Updated', icon: iconFor('level', postText) };
    if (postText.includes('unit'))
      return { label: 'Unit Updated', icon: iconFor('unit', postText) };
    if (postText.includes('accommodation'))
      return { label: 'Accommodation Updated', icon: iconFor('accommodation', postText) };
    if (postText.includes('partner'))
      return { label: 'Partner Created', icon: iconFor('partner', postText) };
    if (postText.includes('service provider'))
      return { label: 'Service Provider Created', icon: iconFor('service', postText) };
    if (postText.includes('user'))
      return { label: 'User Created', icon: iconFor('user', postText) };
    if (postText.includes('lead'))
      return { label: 'Lead Created', icon: iconFor('lead', postText) };
  }

  return null;
};

// "With A, B and 2 others", from the users tagged when the post was made.
export const TaggedPeople = ({ post, max = 3 }) => {
  const users = (post?.tagPostMapping || []).map((m) => m?.user).filter((u) => u?.name);
  if (users.length === 0) return null;
  const shown = users.slice(0, max);
  const rest = users.length - shown.length;
  return (
    <p
      className="flex items-center gap-1.5 text-xs text-gray-500"
      title={users.map((u) => u.name).join(', ')}
    >
      <AtSign size={13} className="shrink-0" />
      <span className="truncate">
        With{' '}
        {shown.map((u, i) => (
          <React.Fragment key={u.id}>
            {i > 0 && (i === shown.length - 1 && !rest ? ' and ' : ', ')}
            <span className="font-medium text-gray-700">{u.name}</span>
          </React.Fragment>
        ))}
        {rest > 0 && ` and ${rest} ${rest === 1 ? 'other' : 'others'}`}
      </span>
    </p>
  );
};

// Asks before a post changes audience. Portalled to the body, and clicks are
// stopped so they don't reach the card underneath (which opens the post).
export const VisibilityConfirm = ({ toPublic, busy, onConfirm, onCancel }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const Icon = toPublic ? Globe : Lock;
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-gray-900/40 p-4"
      onClick={(e) => {
        e.stopPropagation();
        onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="visibility-confirm-title"
        className="w-full max-w-sm rounded-panel bg-surface p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-100 text-brand-700">
          <Icon size={20} />
        </span>
        <h2 id="visibility-confirm-title" className="mt-4 text-base font-semibold text-gray-900">
          {toPublic ? 'Make this post public?' : 'Make this post private?'}
        </h2>
        <p className="mt-1.5 text-sm text-gray-600">
          {toPublic
            ? 'Buyers following this project will be able to see it.'
            : 'Only your team will be able to see it. Buyers will no longer see it.'}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            autoFocus
            onClick={onCancel}
            className="rounded-control border border-hairline-strong px-4 py-2 text-sm font-medium text-gray-700 hover:bg-surface-sunken"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="rounded-control bg-brand-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600 disabled:opacity-60"
          >
            {toPublic ? 'Make public' : 'Make private'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const MentionMenu = ({ users, onPick, className = '' }) => (
  <div
    className={`absolute z-20 flex max-h-[300px] w-full min-w-[220px] flex-col overflow-y-auto rounded-panel border border-hairline bg-surface p-1 shadow-lg ${className}`}
    onClick={(e) => e.stopPropagation()}
  >
    {users.map((u) => (
      <button
        type="button"
        key={u.id}
        className="flex w-full items-center gap-3 rounded-action px-2 py-1.5 text-left hover:bg-surface-sunken"
        onClick={(e) => {
          e.stopPropagation();
          onPick(u);
        }}
      >
        <Avatar name={u.name} size="24" round />
        <span className="text-sm text-gray-800">{u.name}</span>
      </button>
    ))}
  </div>
);

// Only the author can change or edit a post.
export const isPostAuthor = (post, userId) =>
  !!userId && post?.created_by != null && String(post.created_by) === String(userId);

// Counts come from the API; comments.length is a fallback for older responses.
export const initialCommentCount = (post) =>
  post?.comment_count ?? (Array.isArray(post?.comments) ? post.comments.length : 0);

// One Mixpanel "card viewed" per post per page load, when it is actually on
// screen, not one per card mounted.
const viewedCards = new Set();

const MenuItem = ({ icon: Icon, children, onClick, disabled, danger }) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled}
    onClick={onClick}
    className={`flex w-full items-center gap-2.5 rounded-action px-2.5 py-2 text-left text-sm hover:bg-surface-sunken disabled:opacity-50 ${
      danger ? 'text-danger-fg' : 'text-gray-700'
    }`}>
    <Icon size={15} />
    {children}
  </button>
);

// The card holds no copy of the counts: the list owns the post and applies
// socket updates to it, and the card reports its own changes through
// onChanged(patch). onComment opens the post page at its comment box; comments
// are written there, not on the card. onEdit opens the edit form, onHidden and
// onPinnedChange tell the list the post left this feed or moved.
function LiveFeedCard({ data, onChanged, onComment, onEdit, onHidden, onPinnedChange }) {
  const userId = Cookies.get('userId');
  const isAuthor = isPostAuthor(data, userId);
  const isStaff = Cookies.get('userRole') === 'builder';
  const isPublic = !!data?.is_public;
  const likes = data?.like_count || 0;
  const isLiked = !!data?.is_liked;
  const commentCount = initialCommentCount(data);

  const [busy, setBusy] = useState(false);
  const liking = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmVisibility, setConfirmVisibility] = useState(false);
  const [reporting, setReporting] = useState(false);
  const menuRef = useRef(null);
  const articleRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (e.type === 'keydown' ? e.key === 'Escape' : !menuRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [menuOpen]);

  useEffect(() => {
    const el = articleRef.current;
    const postId = data?.id;
    if (!el || postId == null || viewedCards.has(postId)) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || viewedCards.has(postId)) return;
        viewedCards.add(postId);
        mixpanel.track('Live Feed Card Viewed', {
          user_id: userId,
          post_id: postId,
          timestamp: new Date().toISOString()
        });
        observer.disconnect();
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [data?.id, userId]);

  const tagInfo = tagInfoFor(data);
  const TagIcon = tagInfo?.icon;

  // Helper to check if file is an image
  const isImage = (fileNameOrUrl = '') => /\.(jpe?g|png|gif|webp)$/i.test(fileNameOrUrl);

  // Separate attachments
  const imageAttachments = data?.attachments?.filter((attach) => isImage(attach.name));
  const fileAttachments = data?.attachments?.filter((attach) => !isImage(attach.name));

  // Slider settings
  const sliderSettings = {
    dots: true, // Show dots
    arrows: false, // Hide arrows
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1
  };

  const errorText = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

  // Runs one menu action with the buttons disabled; the toast says how it went.
  const act = async (action, done, failure) => {
    setMenuOpen(false);
    setBusy(true);
    try {
      await action();
      if (done) toast.success(done);
      return true;
    } catch (error) {
      console.error(failure, error);
      toast.error(errorText(error, failure));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Only the fields being changed, never the whole post with its relations.
  const changeVisibility = async () => {
    const next = !isPublic;
    const ok = await act(
      () => editPost({ id: data?.id, is_public: next }),
      `Post visibility updated to ${next ? 'Public' : 'Private'}`,
      'Failed to update post visibility'
    );
    setConfirmVisibility(false);
    if (ok) onChanged?.({ is_public: next });
  };

  const togglePinned = async () => {
    const next = !data?.is_pinned;
    const ok = await act(
      () => setPostPinned(data?.id, next),
      next ? 'Pinned to the top of the feed' : 'Unpinned',
      next ? 'Failed to pin the post' : 'Failed to unpin the post'
    );
    if (ok) {
      onChanged?.({ is_pinned: next });
      onPinnedChange?.();
    }
  };

  const hide = async () => {
    const ok = await act(() => setPostHidden(data?.id, true), null, 'Failed to hide the post');
    if (ok) onHidden?.();
  };

  const report = async (reason) => {
    const ok = await act(
      () => reportPost(data?.id, reason),
      'Thanks. The post has been reported.',
      'Failed to report the post'
    );
    if (ok) setReporting(false);
  };

  const handleLike = async () => {
    if (liking.current) return;
    liking.current = true;
    try {
      if (isLiked) {
        await deleteLike(data?.id);
        onChanged?.({ is_liked: false, like_count: Math.max(0, likes - 1) });
      } else {
        await createLike({ post_id: data?.id });
        onChanged?.({ is_liked: true, like_count: likes + 1 });
      }
    } catch (error) {
      console.error('Error handling like:', error);
      toast.error(errorText(error, 'Failed to update like status'));
    } finally {
      liking.current = false;
    }
  };

  // The feed is ordered by when the post was made; a visibility toggle bumps
  // updated_at and must not make an old post read as new.
  const postedAt = data?.created_at || data?.updated_at;
  const tone = tagInfo ? toneForAction(tagInfo.label) : 'neutral';

  const copyLink = async () => {
    setMenuOpen(false);
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/builder/post/${data?.id}`);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  return (
    <>
      {confirmVisibility && (
        <VisibilityConfirm
          toPublic={!isPublic}
          busy={busy}
          onConfirm={changeVisibility}
          onCancel={() => setConfirmVisibility(false)}
        />
      )}
      {reporting && (
        <ReportPostDialog busy={busy} onSubmit={report} onCancel={() => setReporting(false)} />
      )}
      <article
        ref={articleRef}
        className={`flex h-full w-full cursor-pointer flex-col rounded-panel border border-l-4 border-hairline bg-surface shadow-sm transition-shadow duration-200 hover:shadow-md ${TONE_EDGE[tone]}`}
      >
        <div className="flex-1 p-5">
          {/* Header */}
          <header className="flex items-start gap-3">
            <Avatar
              name={data?.creater_name}
              src={data?.created_by_dp_url || undefined}
              size="40"
              round
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">{data?.creater_name}</p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500">
                {postedAt && (
                  <time dateTime={postedAt} title={fullTimestamp(postedAt)}>
                    {timeAgo(postedAt)}
                  </time>
                )}
                {postedAt && <span aria-hidden="true">·</span>}
                <span
                  className="inline-flex items-center gap-1"
                  title={
                    isPublic
                      ? 'Buyers following this project can see it'
                      : 'Only your team can see it'
                  }
                >
                  {isPublic ? <Globe size={12} /> : <Lock size={12} />}
                  {isPublic ? 'Public' : 'Private'}
                </span>
                {data?.is_pinned && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="inline-flex items-center gap-1 font-medium text-brand-700">
                      <Pin size={12} />
                      Pinned
                    </span>
                  </>
                )}
              </p>
            </div>
            {data?.project_name && (
              <span
                title={data.project_name}
                className="inline-flex max-w-[45%] shrink-0 items-center gap-1.5 rounded-pill bg-surface-sunken px-2.5 py-1 text-xs font-medium text-gray-600"
              >
                <Building2 size={13} className="shrink-0" />
                <span className="truncate">{data.project_name}</span>
              </span>
            )}
            <div className="relative shrink-0" ref={menuRef} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                aria-label="Post options"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
                className="rounded-action p-1 text-gray-500 hover:bg-surface-sunken hover:text-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600"
              >
                <MoreVertical size={18} />
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-20 mt-1 w-56 rounded-panel border border-hairline bg-surface p-1 shadow-lg"
                >
                  {isAuthor && (
                    <MenuItem
                      icon={Pencil}
                      disabled={busy}
                      onClick={() => {
                        setMenuOpen(false);
                        onEdit?.();
                      }}
                    >
                      Edit post
                    </MenuItem>
                  )}
                  {isAuthor && (
                    <MenuItem
                      icon={isPublic ? Lock : Globe}
                      disabled={busy}
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirmVisibility(true);
                      }}
                    >
                      {isPublic ? 'Make private' : 'Make public'}
                    </MenuItem>
                  )}
                  {isStaff && (
                    <MenuItem icon={data?.is_pinned ? PinOff : Pin} disabled={busy} onClick={togglePinned}>
                      {data?.is_pinned ? 'Unpin' : 'Pin to top'}
                    </MenuItem>
                  )}
                  <MenuItem icon={Link2} onClick={copyLink}>
                    Copy link
                  </MenuItem>
                  <MenuItem icon={EyeOff} disabled={busy} onClick={hide}>
                    Hide from my feed
                  </MenuItem>
                  {!isAuthor && (
                    <MenuItem
                      icon={Flag}
                      danger
                      disabled={busy}
                      onClick={() => {
                        setMenuOpen(false);
                        setReporting(true);
                      }}
                    >
                      Report post
                    </MenuItem>
                  )}
                </div>
              )}
            </div>
          </header>

          {/* Content */}
          <div className="mt-4 flex flex-col gap-3">
            {tagInfo && (
              <span
                className={`inline-flex w-fit items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-medium ${TONE_SOFT[tone]}`}
              >
                <TagIcon size={13} />
                {tagInfo.label}
              </span>
            )}

            {data?.post && (
              <p className="whitespace-pre-line break-words text-[15px] leading-relaxed text-gray-800">
                <MentionText text={data.post} />
              </p>
            )}

            <TaggedPeople post={data} />

            {/* Image Slider or Single Image */}
            {imageAttachments && imageAttachments.length > 0 && (
              <div className="relative overflow-hidden rounded-control bg-gray-900">
                {imageAttachments.length === 1 ? (
                  <img
                    src={imageAttachments[0].url}
                    alt={imageAttachments[0].name}
                    className="h-[368px] w-full object-contain"
                  />
                ) : (
                  <Slider {...sliderSettings}>
                    {imageAttachments.map((attach) => (
                      <div key={attach.id} className="flex items-center justify-center">
                        <img
                          src={attach.url}
                          alt={attach.name}
                          className="h-auto w-full object-contain"
                        />
                      </div>
                    ))}
                  </Slider>
                )}
              </div>
            )}

            {/* Files */}
            {fileAttachments && fileAttachments.length > 0 && (
              <ul className="flex flex-col gap-2">
                {fileAttachments.map((file) => (
                  <li
                    key={file.id}
                    className="flex items-center gap-3 rounded-control border border-hairline bg-surface-muted px-3 py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Paperclip size={15} className="shrink-0 text-gray-400" />
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-w-0 flex-1 truncate text-sm text-brand-700 hover:underline"
                    >
                      {file.name}
                    </a>
                    <a
                      href={file.url}
                      download={file.name}
                      aria-label={`Download ${file.name}`}
                      className="rounded-action p-1 text-gray-500 hover:bg-surface-sunken hover:text-gray-800"
                    >
                      <Download size={15} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Actions. The like count opens who liked it. */}
        <div className="grid grid-cols-2 gap-1 border-t border-hairline px-3 py-1.5">
          <div className="flex items-center justify-center rounded-control transition-colors hover:bg-surface-sunken">
            <button
              type="button"
              aria-pressed={isLiked}
              onClick={(e) => {
                e.stopPropagation();
                handleLike();
              }}
              className={`flex items-center gap-2 py-2 pl-2 text-sm font-medium ${
                likes > 0 ? 'pr-1' : 'pr-2'
              } ${isLiked ? 'text-brand-700' : 'text-gray-600'}`}
            >
              <ThumbsUp size={17} fill={isLiked ? 'currentColor' : 'none'} />
              Like
            </button>
            <LikersPopover
              postId={data?.id}
              count={likes}
              label={`· ${likes}`}
              buttonClassName="py-2 pr-2 text-sm text-gray-500 hover:text-brand-700 hover:underline"
            />
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onComment?.();
            }}
            className="flex items-center justify-center gap-2 rounded-control py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-surface-sunken"
          >
            <MessageSquare size={17} />
            Comment
            {commentCount > 0 && <span className="text-gray-500">· {commentCount}</span>}
          </button>
        </div>
      </article>
    </>
  );
}

export default LiveFeedCard;
